import React from 'react';
import { getLanguage } from './searchHelpers';

export type ContentTypeLike = { innholdstype?: string | null; eksamenstype?: string | null; sidetype?: string | null } | null | undefined;

export function getContentType(result: ContentTypeLike): string | undefined {
    if (!result) return undefined;
    if (result.innholdstype && result.innholdstype.trim() !== "") {
        if((result.sidetype === "LargeDocumentChapter" || result.sidetype === "LargeDocumentChapterPage") && result.innholdstype === "Rundskriv"){
            return "Kapittel i rundskriv"; 
        }
        else if((result.sidetype === "LargeDocumentChapter" || result.sidetype === "LargeDocumentChapterPage") && result.innholdstype === "Veiledning"){
            return "Kapittel i veiledning"; 
        }
        return result.innholdstype;
    } else if (result.eksamenstype && result.eksamenstype.trim() !== "") {
        return result.eksamenstype;
    }
    return undefined;
}

type LastModifiedLike = { datoendret?: string | null } | null | undefined;
export function getLastModified(result: LastModifiedLike): string | undefined {
    if (!result || !result.datoendret) return undefined;
    try {
        const d = new Date(result.datoendret);
        if (isNaN(d.getTime())) return undefined;
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}.${mm}.${yyyy}`;
    } catch {
        return undefined;
    }
}

type ExamPeriodLike = { periode?: string | null } | null | undefined;
export function getExamPeriod(result: ExamPeriodLike): string | undefined {
    if (!result || !result.periode) return undefined;
    return result.periode;
}

export type SpellingSuggestionLike = {
    spellingSuggestion?: string | null;
    items?: unknown[] | null;
} | null | undefined;

/**
 * Har søket et stavforslag som bør brukes til et automatisk omsøk?
 *
 * Samme betingelse som runQuery bruker, skilt ut fordi startsøket med
 * filtervalues=all går utenom runQuery på læreplan-, eksamens- og
 * forskningssiden - der ble stavekorrigeringen aldri kjørt, og brukeren måtte
 * trykke Søk selv (UDIRSOK-740).
 */
export function hasUsableSpellingSuggestion(result: SpellingSuggestionLike, queryString: string): boolean {
    const forslag = result?.spellingSuggestion;
    if (!forslag || forslag === queryString) return false;
    return (result?.items?.length ?? 0) === 0;
}

export type IntroTitleLike = { introtitle?: string | null } | null | undefined;
export function getIntroTitleHtml(result: IntroTitleLike): string | undefined {
    if (!result || result.introtitle == null) return undefined;
    const t = result.introtitle.trim();
    if (t === "") return undefined;
    return result.introtitle;
}

type TeaserLike = {
    Url?: string | null;
    ContentSource?: string | null;
    hhteaser?: string | null;
    hhsummary?: string | null;
} | null | undefined;

export function getTeaser(result: TeaserLike): React.ReactElement[] | null {
    if (!result) return null;
    const url = result.Url || '';
    if (url.indexOf('https://kandidat') === 0 || url.indexOf('https://eksamen') === 0 || url.indexOf('https://testuespudir') === 0) {
        return null;
    }
    const language_attr = getLanguage(result as any);
    const elements: React.ReactElement[] = [];
    const hhteaser = (result.hhteaser || '').replace(/&nbsp/g, ' ').trim();

    if (result.ContentSource === 'UdirNo') {
        elements.push(<p className="teaser" key="resultData-teaser-1" {...language_attr} dangerouslySetInnerHTML={{ __html: result.hhsummary || '' }} />);
    } else if ((!hhteaser || hhteaser === '') && result.hhsummary) {
        elements.push(<p className="teaser" key="resultData-teaser-2" {...language_attr} dangerouslySetInnerHTML={{ __html: result.hhsummary }} />);
    } else {
        elements.push(<p className="teaser" key="resultData-teaser-3" {...language_attr} dangerouslySetInnerHTML={{ __html: result.hhteaser || '' }} />);
    }

    return elements;
}

export function getTeaserText(result: TeaserLike): { __html: string } | null {
    if (!result) return null;
    const url = result.Url || '';
    if (url.indexOf('https://kandidat') === 0 || url.indexOf('https://eksamen') === 0 || url.indexOf('https://testuespudir') === 0) {
        return null;
    }
    const hhteaser = (result.hhteaser || '').replace(/&nbsp/g, ' ').trim();

    let html: string | null = null;
    if (result.ContentSource === 'UdirNo') {
        html = result.hhsummary || null;
    } else if ((!hhteaser || hhteaser === '') && result.hhsummary) {
        html = result.hhsummary;
    } else {
        html = result.hhteaser || null;
    }

    if (!html) return null;
    return { __html: html };
}

type FoRTeaserLike = {
    description?: string | null;
    hhsummary?: string | null;
} | null | undefined;

export function getFoRTeaser(result: FoRTeaserLike & Record<string, any>): React.ReactElement[] {
    const elements: React.ReactElement[] = [];
    if (!result) return elements;
    const language_attr = getLanguage(result as any);
    const hasDescription = result.description !== null && result.description !== undefined
        && result.description !== '' && result.description !== '&nbsp;' && result.description !== '&nbsp;\n&nbsp;';
    const qs = typeof window !== 'undefined' ? window.location.search : '';
    const isEmptySearch = qs === '' || qs === '?query=' || qs === '?query=*' || qs.indexOf('query=*') !== -1 || qs.indexOf('query=') === -1;
    if (hasDescription) {
        // Bruk highlightet description ved aktivt søk, ellers ren description
        const descriptionHtml = (!isEmptySearch && result.hhdescription) ? result.hhdescription : result.description as string;
        elements.push(<div key="teaser-1" {...language_attr} dangerouslySetInnerHTML={{ __html: descriptionHtml }} />);
    } else if (!isEmptySearch && result.hhsummary) {
        // Ingen description: fall tilbake til dynamisk hhsummary, men kun ved aktivt søk
        elements.push(<div key="teaser-2" {...language_attr} dangerouslySetInnerHTML={{ __html: result.hhsummary }} />);
    }
    return elements;
}
